import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog, faDownload, faLayerPlus, faPlus,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, IconWithMenu, Link,
} from '@domain-group/suit-theme'

import AuthenticatedDownload from '@components/AuthenticatedDownload'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import { useFilters } from '@domain-group/react-hooks'

import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useAgency'
import useApiKey from '@hooks/useApiKey'
import useApiKeys from '@hooks/useApiKeys'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import ApiKeyListItem from './ApiKeyListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'User Name',
    },
  ]
}

type ApiKeysProps = {
  disabled?: boolean,
  filters: {
    default_user_id?: number,
    owner_id?: number,
    owner_type?: string,
    string?: string,
  },
  hideFilters: boolean,
  userSearchRequestOptions: {},
}

const ApiKeys = (props: ApiKeysProps) => {
  const {
    disabled = false, filters, hideFilters, userSearchRequestOptions,
  } = props

  const { default_user_id } = filters || {}

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const apiKeysPayload = useApiKeys({
    // @ts-expect-error TS2322
    filters: {
      ...filters,
      ...pageFilters,
    },
    performHttpRequests: true,
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredApiKeys,
    filteredApiKeysCount,
    hasFilteredApiKeys,
    loading,
  } = apiKeysPayload

  const apiKeyPayload = useApiKey(filters)
  const {
    callbacks: { createOrEditApiKey, manageBulkCreateApiKeys },
  } = apiKeyPayload

  const { selectedAgency: agency } = useAgencySelector()

  const { exportAgencyApiKeys } = generateUrls(agency)

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-API-keys' }}
      headerRight={!hideFilters && (
        <DashboardModule.FilterHeader {...filterPayload} />
      )}
      loading={loading}
      title="API Keys"
    >

      <Box flexDirection="column">
        <DashboardModuleFilters
          filterFields={buildPageFilterFields()}
          {...filterPayload}
        />

        {!loading && !hasFilteredApiKeys && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No API Keys present
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              Please adjust your filters or assign an API key.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredApiKeys && (
          <DashboardModule.ScrollableSection>
            {filteredApiKeys.map(apiKey => (
              <ApiKeyListItem
                apiKey={apiKey}
                disabled={disabled}
                key={apiKey.id}
                showUserName={!default_user_id}
                userSearchRequestOptions={userSearchRequestOptions}
              />
            ))}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredApiKeysCount}
          loading={loading}
        >
          <Button
            buttonStyle="secondaryEdit"
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditApiKey({ userSearchRequestOptions })}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Add API Key
          </Button>

          <ScopedConditionalContent permittedRoleTitles={['Office Administrator']}>
            <IconWithMenu
              boxProps={{ display: ['none', 'flex'], marginLeft: 'medium', alignItems: 'center' }}
              icon={(
                <Link greyLink>
                  <FontAwesomeIcon icon={faCog} />
                </Link>
                )}
            >
              {/*
               // @ts-expect-error TS2741 */}
              <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
                {!disabled && (
                  <Link key="bulk_create" onClick={() => manageBulkCreateApiKeys()}>
                    <FontAwesomeIcon icon={faPlus} /> Bulk Create API Keys
                  </Link>
                )}

                <AuthenticatedDownload>
                  <Link key="download" href={exportAgencyApiKeys}>
                    <FontAwesomeIcon icon={faDownload} /> Download List
                  </Link>
                </AuthenticatedDownload>
              </IconWithMenu.Menu>
            </IconWithMenu>

            <Box display={['flex', 'none']} marginTop="medium" flexDirection="column">
              {!disabled && (
                <Button
                  buttonStyle="secondaryEdit"
                  icon={<FontAwesomeIcon icon={faLayerPlus} />}
                  onClick={() => manageBulkCreateApiKeys()}
                  size="medium"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Bulk Create API Keys
                </Button>
              )}

              <AuthenticatedDownload>
                <Button
                  // @ts-expect-error TS2322
                  as="a"
                  buttonStyle="secondaryEdit"
                  href={exportAgencyApiKeys}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  marginTop="medium"
                  size="medium"
                >
                  Download List
                </Button>
              </AuthenticatedDownload>
            </Box>
          </ScopedConditionalContent>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

export default ApiKeys
